import * as angular from 'angular';

import datafangstHelpers from '../utilities';
import FeatureListSummary from './model-featurelistsummary';
import {uniqueArrayElements} from "../array";
import datacatalog from "../integration/datacatalog/datacatalog-services";
import contractService from "../service/contract/contract-services";
import {Destination} from "../integration/representation/contract";
import {ValidationIssue} from "../integration/representation/validation-issue";
import {Feature} from "../integration/representation/feature";

export type FeatureByTypeId = { [key: number]: Feature[] }

interface BasicFeatureType {
    id: number;
    name: string;
    sortNum: number;
}

interface IssuesBySeverity {
    errors: ValidationIssue[];
    warnings: ValidationIssue[];
    notabenes: ValidationIssue[];
}

type IssuesByTypeId = { [key: number]: IssuesBySeverity };

export default class FeatureList {
    readonly features: FeatureByTypeId;
    private presentFeatureTypeIds: number[];
    private missingFeatureTypes: BasicFeatureType[];
    private summary: FeatureListSummary;
    private issues: IssuesByTypeId;
    readonly objectType: Destination;
    readonly featureTypeIssues: { [key: number]: ValidationIssue[] };

    constructor(datacatalog: datacatalog, featuresFromServer: Feature[], contract: contractService,
                objectType: Destination, featureTypeIssues: { [key: number]: ValidationIssue[] }) {
        this.features = {};
        this.presentFeatureTypeIds = [];
        this.missingFeatureTypes = [];
        this.issues = {};
        this.objectType = objectType || 'NVDB';
        this.featureTypeIssues = featureTypeIssues || {};
        this.summary = new FeatureListSummary({}, datacatalog, this.objectType);

        if (featuresFromServer && contract) {
            this.features = this.convertFeatureArrayToMap(featuresFromServer);
            this.presentFeatureTypeIds = this.getPresentFeatureTypeIds(featuresFromServer);
            this.missingFeatureTypes = this.getMissingFeatureTypes(featuresFromServer, contract, datacatalog, this.objectType);
            this.summary = new FeatureListSummary(this.features, datacatalog, objectType);
            this.issues = this.findIssues(this.features);
        }
    }

    isFeatureIdPresent(featuredId: string): boolean {
        for (let key in this.features) {
            const featuresOfOneType: Feature[] = this.features[key];
            if (featuresOfOneType.some(feature => feature.id === featuredId)) {
                return true;
            }
        }
        return false;
    }

    updateIssueCount(){
        this.issues = this.findIssues(this.features);
    }

    replaceFeatures(changedFeatures: Feature[]) {
        for (let i = 0; i < changedFeatures.length; i++){
            this.replaceFeature(changedFeatures[i]);
        }
        this.issues = this.findIssues(this.features);
    }

    getFeatureById(featureId: string): Feature {
        let found: undefined;
        const featureTypes = datafangstHelpers.values(this.features);
        for (let i = 0; i < featureTypes.length; i++) {
            const features = featureTypes[i];
            for (let j = 0; j < features.length; j++) {
                const feature = features[j];
                if (feature.id == featureId) {
                    return feature;
                }
            }
        }

        return found;
    }

    getFeaturesByType(types: number | number[]): Feature[] {
        let result: Feature[] = [];
        if (Array.isArray(types)) {
            const features = this.features;
            const listOfArrays = types.map((type) => features[type]);
            result = result.concat.apply(result, listOfArrays); // Flatten list of arrays
        } else {
            result = this.features[types];
        }
        if (result) {
            result = result.filter(type => type !== undefined);
        }
        return result;
    }

    getUnregisteredFeaturesByType(featureTypeId: number): Feature[] {
        return this.features[featureTypeId].filter(feature => {
            return feature.dirty;
        });
    }

    replaceFeature(feature: Feature): void {
        const list = this.features[feature.typeId];
        for (let i = 0; i < list.length; i++) {
            if (feature.id == list[i].id) {
                list[i] = feature;
            }
        }
    }

    convertFeatureArrayToMap(featureArray: Feature[]): FeatureByTypeId {
        const features: FeatureByTypeId = {};
        featureArray.forEach(function(feature) {
            const featureId = feature.typeId;
            if (!features[featureId]) {
                features[featureId] = [];
            }
            features[featureId].push(feature);
        });
        return features;
    }

    getPresentFeatureTypeIds(featureArray: Feature[]): number[] {
        return uniqueArrayElements(
            featureArray.map(feature => feature.typeId));
    }

    getMissingFeatureTypes(featureArray: Feature[], contract: contractService,
                           datacatalog: datacatalog, objectType: Destination): BasicFeatureType[] {
        // Array of unique feature type ids currently present in contract
        const presentFeatureTypeIds = this.getPresentFeatureTypeIds(featureArray);

        let missingFeatureTypes: BasicFeatureType[] = [];

        if (contract) {

            const objectList = objectType === 'NVDB' ? contract.objectList : contract.fkbObjectList;
            // Array of unique feature type ids in contract object list
            const objectListFeatureTypeIds = objectList.map(type => type.typeId);

            missingFeatureTypes = objectListFeatureTypeIds
                .filter(ftid => presentFeatureTypeIds.indexOf(ftid) === -1)
                .map(function(ftid) {
                    const featureType = datacatalog.getFeatureTypeWithDestination(ftid, objectType) || FeatureList.notInDatacatalog(ftid, objectType);
                    return {
                        id: ftid,
                        name: featureType.name,
                        sortNum: featureType.sortNum
                    };
                });
        }
        return missingFeatureTypes;
    }

    static notInDatacatalog(ftid: number, objectType: Destination): BasicFeatureType {
        return {
            id: ftid,
            name: objectType + '-' + ftid,
            sortNum: -1
        };
    }

    findIssues(featureMap: FeatureByTypeId): IssuesByTypeId {
        const issues: IssuesByTypeId = {};
        function addIssue(issue: ValidationIssue, typeId: number) {
            if (issue.resolution === null) {
                const featureTypeIsseus = issues[typeId];
                if (issue.severity == 'ERROR') featureTypeIsseus.errors.push(issue);
                if (issue.severity == 'WARNING') featureTypeIsseus.warnings.push(issue);
                if (issue.severity == 'NOTABENE') featureTypeIsseus.notabenes.push(issue);
            }
        }

        angular.forEach(featureMap, function(list: Feature[], typeId: number) {
            issues[typeId] = {
                errors: [],
                warnings: [],
                notabenes: []
            };
            angular.forEach(list, function(feature: Feature) {
                angular.forEach(feature.issues, function(issue) {
                    addIssue(issue, typeId);
                });
                angular.forEach(feature.attributes, function(attribute) {
                    angular.forEach(attribute.issues, function(issue) {
                        addIssue(issue, typeId);
                    });
                });
            });
        });
        return issues;
    }

    issuesForType(typeId: number): IssuesBySeverity {
        return this.issues[typeId];
    }
}
